/**
 * useTutorAvatarState — « Changer ma photo » de la feuille de profil : le menu de l'avatar du tuteur,
 * et son écriture.
 *
 * Port de `CustomizableAvatar.js`, branche tuteur : « Choisir une image » ouvre la galerie, recadrée
 * au carré sur mobile (`allowsEditing`, `aspect: [1, 1]`), et envoie la photo à
 * `updateAvatarSelected` (`avatar` + `avatar_characters: "image"`) ; « Réinitialiser » confirme puis
 * envoie les deux à vide. Pendant l'envoi, la ligne de la feuille tourne (`uploading`) ; un refus se
 * dit dans le `<Snackbar>` de la coquille au lieu de l'ancienne boîte.
 *
 * Sur le web, l'ancien écran recadrait la photo dans sa modale (`ImagePickerCropModal`,
 * `react-avatar-image-cropper`), qui rendait une petite image. La galerie du navigateur rend la
 * photo entière, que le serveur refusait au-delà de quelques Mo (413) : elle est recadrée au carré
 * central et réduite avant de partir (`prepareAvatarImage`), sans geste de plus.
 */
import * as ImagePicker from "expo-image-picker"
import moment from "moment"
import { useCallback, useState } from "react"
import { useSelector } from "react-redux"

import { authService } from "../../../../../../data/services/authService"
import type { AccountAvatarMenuStep } from "../../../../../../design-system/tutor"
import {
    AVATAR_IMAGE_CHARACTERS,
    AVATAR_RESET_PAYLOAD,
    tutorAvatarFileName,
} from "../../../../adapters/tutor/Account/tutorAvatarAdapter"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { uploadPart } from "../../student/Messaging/conversationFiles"
import { prepareAvatarImage } from "../../../hooks/avatarImage"

export const AVATAR_FAILED = "Ta photo n'a pas pu être mise à jour. Réessaie dans un instant."

/**
 * La photo choisie, dans la forme que `FormData` sait envoyer sur chaque plateforme (`uploadPart`,
 * celle des pièces de la messagerie). Son type voyage avec elle (`mimeType` de la galerie) : sur le
 * web, l'adresse est un `blob:` qui ne le dit pas. `null` quand le fichier ne se lit plus.
 */
async function avatarFormData(
    uri: string,
    name: string,
    mimeType: string | null | undefined
): Promise<FormData | null> {
    const part = await uploadPart({ uri, name, type: mimeType || "image/png" })
    if (part == null) return null
    const form = new FormData()
    // Sur mobile, `FormData` prend l'objet `{ uri, name, type }` de React Native.
    form.append("avatar", part as unknown as Blob)
    form.append("avatar_characters", AVATAR_IMAGE_CHARACTERS)
    return form
}

export function useTutorAvatarState() {
    const [step, setStep] = useState<AccountAvatarMenuStep>("closed")
    const [error, setError] = useState<string | null>(null)
    const displayName = useSelector(
        (state: unknown) => (selectAccount(state as never) as { display_name?: string } | undefined)?.display_name
    )

    const { start, requesting, gesture } = useApiMutation({
        service: authService.updateAvatarSelected,
        gesture: "account.avatar",
        surface: "tutor_profile_sheet",
        fallback: AVATAR_FAILED,
        skipWhilePending: true,
        onError: (message) => setError(message),
    })

    const pickImage = useCallback(async () => {
        setStep("closed")
        try {
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ["images"],
                allowsEditing: true,
                aspect: [1, 1],
                // Une photo de téléphone en qualité 1 pèse plusieurs Mo : le serveur la refuserait.
                quality: 0.8,
            })
            const asset = result.canceled ? null : result.assets?.[0]
            if (asset?.uri == null) return
            setError(null)
            const image = await prepareAvatarImage({ uri: asset.uri, mimeType: asset.mimeType })
            const form = await avatarFormData(
                image.uri,
                tutorAvatarFileName(displayName, moment(), image.mimeType),
                image.mimeType
            )
            image.dispose()
            if (form == null) setError(AVATAR_FAILED)
            else start({}, form as unknown as Record<string, unknown>)
        } catch {
            // La galerie refusée ou la photo illisible : rien n'est parti, la coquille le dit.
            setError(AVATAR_FAILED)
        }
    }, [displayName, start])

    return {
        /** La ligne « Changer ma photo » : ouvre le menu (la feuille reste ouverte dessous). */
        openMenu: () => {
            gesture.opened()
            setStep("menu")
        },
        uploading: requesting,
        menu: {
            step,
            onClose: () => setStep("closed"),
            onPickImage: () => void pickImage(),
            onAskReset: () => setStep("confirm-reset"),
            onConfirmReset: () => {
                setStep("closed")
                setError(null)
                start({}, AVATAR_RESET_PAYLOAD)
            },
        },
        error,
        dismissError: () => setError(null),
    }
}
